import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  Tab,
  Tabs,
  Container,
  IconButton,
  Paper,
} from "@mui/material";
import { ArrowLeft, Plus, RefreshCw, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useWebSocket from "../../../utils/Sockets";
import SearchAndFilters from "../../../components/SearchAndFilters";
import SearchContentContainer from "../../../components/SearchContentContainer";
import LoadingSkeleton from "../../../components/LoadingSkeleton";
import { InitialSearchStates } from "../../../components/SearchAndFilters/searchStates";
import { filterConfigs } from "../../../components/SearchAndFilters/config";
import { GenerationProgress } from "../../../components/GenerationProgress";
import { config } from "../../../config";
import queryString from "query-string";
import { homeRoutePath, settingsPath } from "../../Router";
import SessionService from "../../../services/Session";
import { useDispatch } from "react-redux";
import { setUser } from "../../../redux/actions/user";
import { logIssueNetworkRequestError } from "../../../utils/Loggers";
import AdStrategyService from "../../../services/AdStrategy";
import ICPSelectionDialog from "./ICPSelectionDialog";
import { useSubscriptionLimit } from "../../../hooks/useSubscriptionLimit";
import SubscriptionLimitDialog from "../../../components/SubscriptionLimitDialog";
import { displayToastMessage } from "../../../utils/Toasts";

const StrategyCard = React.memo(
  ({ strategy, onNavigate, tabValue, onTabChange, index }: any) => {
    const totalBudget: any = useMemo(() => {
      return Object.values(strategy.platforms).reduce(
        (sum: any, platform: any) =>
          sum + (platform?.metrics?.suggestedBudget || 0),
        0
      );
    }, [strategy.platforms]);

    const totalReach: any = useMemo(() => {
      return Object.values(strategy.platforms).reduce(
        (sum: any, platform: any) =>
          sum + (platform?.metrics?.estimatedReach || 0),
        0
      );
    }, [strategy.platforms]);

    const activePlatforms = useMemo(() => {
      return Object.keys(strategy.platforms).length;
    }, [strategy.platforms]);

    return (
      <motion.div
        className="h-full rounded-xl bg-white shadow-md hover:shadow-lg border border-gray-100 hover:-translate-y-1 hover:border-red-100 transition-all duration-300 mb-4 sm:mb-6 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        onClick={() => {
          onNavigate(strategy._id, strategy);
        }}
      >
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderBottom: "1px solid",
            borderColor: "divider",
            background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 2, sm: 0 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                className="mb-1 line-clamp-1 font-semibold"
                sx={{
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  color: "#1F2937",
                }}
              >
                {strategy.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  color: "#6B7280",
                }}
              >
                Created {new Date(strategy.createdAt).toLocaleDateString()}
              </Typography>
            </Box>

            <Button
              startIcon={<Settings className="h-4 w-4" />}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              variant="outlined"
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                borderColor: "rgba(107, 114, 128, 0.3)",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "rgba(107, 114, 128, 0.5)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(strategy._id, strategy);
              }}
            >
              Manage
            </Button>
          </Box>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Typography
                variant="subtitle2"
                sx={{ color: "#6B7280", fontWeight: 500, fontSize: "0.8rem" }}
              >
                Active Platforms
              </Typography>
              <Typography className="line-clamp-1 text-gray-800 font-medium">
                {activePlatforms} Platforms
              </Typography>
            </div>
            <div>
              <Typography
                variant="subtitle2"
                sx={{ color: "#6B7280", fontWeight: 500, fontSize: "0.8rem" }}
              >
                Total Budget
              </Typography>
              <Typography className="line-clamp-1 text-gray-800 font-medium">
                ${totalBudget.toLocaleString()}
              </Typography>
            </div>
            <div>
              <Typography
                variant="subtitle2"
                sx={{ color: "#6B7280", fontWeight: 500, fontSize: "0.8rem" }}
              >
                Estimated Reach
              </Typography>
              <Typography className="line-clamp-1 text-gray-800 font-medium">
                {totalReach.toLocaleString()} Users
              </Typography>
            </div>
            <div>
              <Typography
                variant="subtitle2"
                sx={{ color: "#6B7280", fontWeight: 500, fontSize: "0.8rem" }}
              >
                Last Updated
              </Typography>
              <Typography className="line-clamp-1 text-gray-800 font-medium">
                {new Date(strategy.updatedAt).toLocaleDateString()}
              </Typography>
            </div>
          </div>
        </Box>

        <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => onTabChange(newValue)}
            onClick={(e) => e.stopPropagation()}
            className="px-3"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#DC2626",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.9rem",
                color: "#6B7280",
                "&.Mui-selected": {
                  color: "#DC2626",
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Platforms" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600, color: "#374151" }}
              >
                Platform Distribution
              </Typography>
              <div className="grid grid-cols-2 gap-4">
                {strategy.crossPlatform?.budgetAllocation?.distribution
                  ?.slice(0, 4)
                  .map((dist: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-1"
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#4B5563" }}
                      >
                        {dist.platform}
                      </Typography>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full"
                            style={{ width: `${dist.percentage}%` }}
                          ></div>
                        </div>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6B7280", fontWeight: 500 }}
                        >
                          {dist.percentage}%
                        </Typography>
                      </div>
                    </div>
                  ))}
              </div>
            </Box>
          )}
          {tabValue === 1 && (
            <Box>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(strategy.platforms).map(
                  ([platform, data]: [string, any]) => (
                    <div
                      key={platform}
                      className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "#374151" }}
                      >
                        {platform.replace("Ads", "")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span className="text-gray-500">Budget:</span>
                        <span className="text-gray-800 font-medium">
                          $
                          {data?.metrics?.suggestedBudget?.toLocaleString() ||
                            "..."}
                        </span>
                      </Typography>
                    </div>
                  )
                )}
              </div>
            </Box>
          )}
        </Box>
      </motion.div>
    );
  }
);

const StrategiesList = React.memo(
  ({ data, onNavigate, tabValue, onTabChange }: any) => {
    return (
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
        }}
      >
        <div
          className="grid gap-4 sm:gap-6"
          style={{
            gridTemplateColumns:
              "repeat(auto-fill, minmax(min(100%, 440px), 1fr))",
          }}
        >
          <AnimatePresence>
            {data.map((strategy: any, index: number) => (
              <StrategyCard
                key={strategy._id}
                strategy={strategy}
                onNavigate={onNavigate}
                tabValue={tabValue}
                onTabChange={onTabChange}
                index={index}
              />
            ))}
          </AnimatePresence>
        </div>
      </Container>
    );
  }
);

const AdStrategyView = ({ onBack }: any) => {
  const { projectId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | any>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [strategyData, setStrategyData] = useState<any>({
    strategies: [],
    searchState: InitialSearchStates.strategies,
  });
  const searchState = useMemo(
    () => strategyData.searchState,
    [strategyData.searchState]
  );

  // Progress state
  const [socketConnected, setSocketConnected] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState("");
  const [generationError, setGenerationError] = useState("");
  const [jobState, setJobState] = useState<any>(null);
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState("");
  const statusCheckInterval = useRef<any>(null);

  // Generation dialog state
  const [showIcpDialog, setShowIcpDialog] = useState(false);
  const [icpError, setIcpError] = useState<string>();

  const { dialogProps, makeRequest: makeSubscriptionGatedRequest } =
    useSubscriptionLimit(() => navigate(settingsPath, { replace: true }));

  const fetchStrategies = useCallback(
    async (currentUserId: string | any, searchParams: any) => {
      setLoading(true);
      try {
        if (!config.inProd) {
          setStrategyData((prevData: any) => ({
            strategies: config.mocks.strategies,
            searchState: {
              ...prevData.searchState,
              ...InitialSearchStates.strategies,
            },
          }));
          return;
        }

        const adStrategyService = new AdStrategyService();
        const response: any = await adStrategyService.listStrategies(
          projectId,
          currentUserId,
          {
            // Main query
            name: searchParams.query,

            // Platform
            platform:
              searchParams.filters.platform === "all"
                ? undefined
                : searchParams.filters.platform,

            // Pagination
            page: searchParams.pagination.currentPage,
            limit: searchParams.pagination.limit,

            // Sorting
            sortBy: "createdAt",
            sortOrder: searchParams.sorting.sortOrder === -1 ? "desc" : "asc",

            // Filters
            status:
              searchParams.filters.status === "all"
                ? undefined
                : searchParams.filters.status,
            dateFrom: searchParams.filters.dateFrom,
            dateTo: searchParams.filters.dateTo,
          }
        );
        setStrategyData((prevData: any) => ({
          strategies: response.strategies,
          searchState: {
            ...prevData.searchState,
            ...searchParams,
            pagination: response.pagination,
          },
        }));
      } catch (error: Error | any) {
        setError("Failed to load strategies");
        console.log("error:", error);
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  const handleInitialLoad = useCallback(async () => {
    try {
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      const sessionService = new SessionService();
      const sessionData = await sessionService.reviewSession();
      if (!sessionData.success) {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      dispatch(setUser(sessionData.userSessionData));
      setUserId(sessionData.userSessionData.id);

      await fetchStrategies(sessionData.userSessionData.id, searchState);
    } catch (error: any) {
      logIssueNetworkRequestError("AdStrategyView.handleInitialLoad", error);
      navigate(homeRoutePath, { replace: true });
    }
  }, [dispatch, location, navigate, fetchStrategies]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const onProgress = useCallback((data: any) => {
    if (data.jobType === "adStrategy") {
      setProgress(data.progress);
      setGenerationStage(data.stage);
      setLastUpdateTimestamp(data.timestamp);
      setJobState("in-progress");
      if (data.progress === 100) {
        setJobState("completed");
      }
    }
  }, []);

  const onJobCompleted = useCallback(
    (data: any) => {
      if (data.type === "adStrategy") {
        setProgress(100);
        setJobState("completed");
        setGenerationStage("Strategy Generation Complete");
        setLastUpdateTimestamp(new Date().toISOString());
        if (config.inProd) {
          (window as any).location.reload();
          return;
        }
        setStrategyData((prev: any) => ({
          ...prev,
          strategies: [
            ...prev.strategies,
            {
              _id: String(prev.strategies.length + 1),
              name: `New Strategy ${prev.strategies.length + 1}`,
              platforms: {
                googleAds: { status: "pending", budget: 1000 },
                facebookAds: { status: "pending", budget: 1000 },
              },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ],
        }));
      }
    },
    [fetchStrategies, userId, searchState]
  );

  const { socket, connected } = useWebSocket({
    onProgress,
    onJobCompleted,
    onJobFailed: (error) => {
      setProgress(-1);
      setJobState("error");
      setGenerationError(error.message);
      setLastUpdateTimestamp(new Date().toISOString());
    },
  });

  useEffect(() => {
    if (!connected || !generating) return;

    setSocketConnected(connected);

    // If socket disconnects during generation, start polling
    if (!connected && generating && progress > 0 && progress < 100) {
      startStatusCheck();
    }

    return () => clearStatusCheck();
  }, [connected, generating]);

  const startStatusCheck = () => {
    if (!statusCheckInterval.current) {
      statusCheckInterval.current = setInterval(() => {
        // Implement status check endpoint call here
        fetch(`/api/v1/projects/${projectId}/adstrategy/status`)
          .then((res) => res.json())
          .then((data) => {
            if (data.progress) {
              setProgress(data.progress);
              setGenerationStage(data.stage || "Processing");
            }
          })
          .catch(console.error);
      }, 5000);
    }
  };

  const clearStatusCheck = () => {
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current);
      statusCheckInterval.current = null;
    }
  };

  const handleGenerateClick = () => {
    setIcpError(undefined);
    setShowIcpDialog(true);
  };

  const handleGenerateStrategy = useCallback(
    async (
      userId: string,
      icpId: string,
      modelAPIKey: string,
      additionalGenerationContext: string
    ) => {
      setShowIcpDialog(false);
      setJobState("initializing");
      setProgress(0);
      setGenerationStage("Initializing Strategy Generation");
      setLastUpdateTimestamp(new Date().toISOString());
      setGenerationError("");
      setIcpError(undefined);

      const adStrategyService = new AdStrategyService();

      await makeSubscriptionGatedRequest({
        requestFn: () =>
          adStrategyService.startStrategyGeneration(projectId, {
            icpId,
            userId,
            modelAPIKey,
            socketId: socket?.id,
            priority: 5,
            additionalGenerationContext,
          }),
        onSuccess: (_) => {
          displayToastMessage(
            "success",
            "Strategy generation successfully started"
          );
        },
        onError: (error: any) => {
          console.error("Failed to start strategy generation", error);
          let errorMsg = error.error ? error.error : "";
          setJobState("error");
          setGenerationError(
            `Failed to start strategy generation. Please try again. Error: ${errorMsg}`
          );
          setProgress(-1);
          setLastUpdateTimestamp(new Date().toISOString());
        },
      });
    },
    [projectId, socket, makeSubscriptionGatedRequest]
  );

  const handleSearch = async (searchParams: any) => {
    setStrategyData((prev: any) => ({
      ...prev,
      searchState: searchParams,
    }));
    await fetchStrategies(userId, searchParams);
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#F9FAFB",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: { xs: 3, sm: 4 },
          p: { xs: 2, sm: 3 },
          borderRadius: "12px",
          border: "1px solid rgba(0,0,0,0.05)",
          background: "linear-gradient(to right, #FFFFFF, #F9FAFB)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 2, sm: 2 },
            justifyContent: "space-between",
          }}
        >
          {/* Left section with back button and title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: { xs: 1, sm: 0 },
            }}
          >
            <IconButton
              onClick={() => {
                if (onBack) onBack();
                navigate(`/project/${projectId}`);
              }}
              sx={{
                padding: { xs: 1, sm: 1 },
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "rgba(249, 250, 251, 0.8)",
                },
              }}
            >
              <ArrowLeft className="text-gray-700" />
            </IconButton>
            <div>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  fontWeight: 600,
                  color: "#1F2937",
                }}
              >
                Ad Strategies
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#6B7280",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Manage and optimize your cross-platform campaign strategies
              </Typography>
            </div>
          </Box>

          {/* Right section with buttons */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, sm: 2 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => fetchStrategies(userId, searchState)}
              sx={{
                flex: { xs: 1, sm: "initial" },
                padding: { xs: "8px 16px", sm: "10px 18px" },
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                borderRadius: "8px",
                borderColor: "rgba(107, 114, 128, 0.3)",
                color: "#4B5563",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "rgba(107, 114, 128, 0.5)",
                  backgroundColor: "rgba(249, 250, 251, 0.8)",
                },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={handleGenerateClick}
              disabled={generating}
              sx={{
                flex: { xs: 1, sm: "initial" },
                padding: { xs: "8px 16px", sm: "10px 18px" },
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                background: "linear-gradient(to right, #DC2626, #EF4444)",
                boxShadow: "0 2px 4px rgba(220, 38, 38, 0.25)",
                "&:hover": {
                  background: "linear-gradient(to right, #B91C1C, #DC2626)",
                  boxShadow: "0 3px 6px rgba(220, 38, 38, 0.3)",
                },
              }}
            >
              Generate New Strategy
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
          className="rounded-lg border border-red-200"
        >
          {error}
        </Alert>
      )}

      {/* Generation Progress */}
      {jobState && (
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <GenerationProgress
            title="Generating Ad Strategy"
            description="Please wait while we analyze your project data and generate optimized strategies..."
            progress={progress}
            stage={generationStage}
            timestamp={lastUpdateTimestamp}
            jobType="adStrategy"
            jobState={jobState}
            error={generationError}
          />
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 2, sm: 3 },
          borderRadius: "12px",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <SearchAndFilters
          totalShown={strategyData.strategies.length}
          onSearch={handleSearch}
          pagination={searchState.pagination}
          sorting={searchState.sorting}
          filters={searchState.filters}
          filterConfig={filterConfigs.strategies}
          isLoading={loading}
        />
      </Paper>

      {/* Strategies List */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        <SearchContentContainer
          isLoading={loading}
          data={strategyData.strategies}
          LoadingComponent={LoadingSkeleton}
          ContentComponent={StrategiesList}
          componentProps={{
            onNavigate: (strategyId: string, strategy: any) =>
              navigate(`/project/${projectId}/adstrategy/${strategyId}`, {
                state: { strategy },
              }),
            tabValue,
            onTabChange: setTabValue,
          }}
          loadingText="Loading ad strategies..."
          emptyTitle="No Strategies Found"
          emptyMessage={
            "Try adjusting your search criteria or generate a new strategy"
          }
        />
      </Paper>

      {/* ICP Selection Dialog */}
      <ICPSelectionDialog
        open={showIcpDialog}
        onClose={() => setShowIcpDialog(false)}
        onSubmit={handleGenerateStrategy}
        error={icpError}
      />

      {/* Subscription Limit Dialog */}
      <SubscriptionLimitDialog {...dialogProps} />
    </Box>
  );
};

export default AdStrategyView;
